import L from 'leaflet';

const CARTO_API_KEY = process.env.VUE_APP_CARTO_API_KEY;

const CARTO_URL = `https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}.png?key=${CARTO_API_KEY}`;
const CARTO_ATTRIBUTION = '&copy; <a href="http://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors, &copy; <a href="https://carto.com/attribution">CARTO</a>';

const OSM_URL = 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png';
const OSM_ATTRIBUTION = '&copy; <a href="http://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors';

export function getPrimaryTileConfig() {
    if (CARTO_API_KEY) {
        return {url: CARTO_URL, attribution: CARTO_ATTRIBUTION, maxZoom: 18};
    }
    return {url: OSM_URL, attribution: OSM_ATTRIBUTION, maxZoom: 19};
}

export function getFallbackTileConfig() {
    return {url: OSM_URL, attribution: OSM_ATTRIBUTION, maxZoom: 19};
}

/*
 * CARTO always answers with HTTP 200 even when the API key is missing/invalid -
 * it serves a static "API KEY REQUIRED" placeholder image instead of an error
 * status, so a Leaflet `tileerror` listener never fires for this failure mode.
 * Two different tile coordinates are probed instead: real map tiles are never
 * byte-identical, but the placeholder image is served verbatim every time.
 */
async function isCartoBroken() {
    try {
        const probeUrls = [
            `https://a.basemaps.cartocdn.com/rastertiles/voyager/1/0/0.png?key=${CARTO_API_KEY}`,
            `https://a.basemaps.cartocdn.com/rastertiles/voyager/1/1/0.png?key=${CARTO_API_KEY}`,
        ];
        const responses = await Promise.all(probeUrls.map(url => fetch(url)));
        if (responses.some(response => !response.ok)) {
            return true;
        }
        const buffers = await Promise.all(responses.map(response => response.arrayBuffer()));
        if (buffers[0].byteLength !== buffers[1].byteLength) {
            return false;
        }
        const [a, b] = buffers.map(buffer => new Uint8Array(buffer));
        for (let i = 0; i < a.length; i++) {
            if (a[i] !== b[i]) {
                return false;
            }
        }
        return true;
    } catch (e) {
        return true;
    }
}

/*
 * Verifies the CARTO basemap in the background (non-blocking - the map already
 * rendered with CARTO tiles) and swaps to OpenStreetMap if it turns out broken.
 * No-op when no CARTO key is configured, since the primary layer is already OSM.
 */
export function attachCartoFallback(map, getLayer, setLayer) {
    if (!CARTO_API_KEY) {
        return;
    }
    isCartoBroken().then(broken => {
        if (!broken) {
            return;
        }
        const currentLayer = getLayer();
        const fallback = getFallbackTileConfig();
        const fallbackLayer = L.tileLayer(fallback.url, {
            maxZoom: fallback.maxZoom,
            attribution: fallback.attribution,
        });
        if (currentLayer) {
            map.removeLayer(currentLayer);
        }
        fallbackLayer.addTo(map);
        setLayer(fallbackLayer);
    });
}
